import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { format } from "date-fns";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { CampaignsViewModel } from "../../viewmodels/campaigns/CampaignsViewModel";
import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import SimpleBanner from "../../components/SimpleBanner";
import InputWithSearch from "../../components/controls/InputWithSearch";
import LoadingSvg from "../../components/spinners/LoadingSvg";

function Campaigns(props) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    askDeleteCampaign,
    campaigns,
    emptyModal,
    getCampaigns,
    loading,
    openCreateCampaignSlideOver,
    setEmptyModelOpen,
    setSlideOver,
    setShowSimpleNotification,
    showSimpleNotification,
    slideOver,
  } = CampaignsViewModel(logout);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);

  useEffect(() => {
    getCampaigns(null, 1);
  }, []);

  const handleSearch = () => {
    setFirst(0);
    getCampaigns(searchTerm.trim() || null, 1);
  };

  const handlePagination = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    const nextPage = event.page + 1;
    getCampaigns(searchTerm.trim() || null, nextPage);
  };

  const startDateBody = useCallback((rowData) => {
    try {
      return format(new Date(rowData.start_date), "MMM d, yyyy");
    } catch {
      return rowData.start_date;
    }
  }, []);

  const endDateBody = useCallback((rowData) => {
    try {
      return format(new Date(rowData.end_date), "MMM d, yyyy");
    } catch {
      return rowData.end_date;
    }
  }, []);

  const actionBody = useCallback(
    (rowData) => {
      return (
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => navigate(`/campaigns/${rowData.id}`)}
          >
            Edit
          </button>
          <button
            type="button"
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            onClick={() => askDeleteCampaign(rowData)}
          >
            Delete
          </button>
        </div>
      );
    },
    [askDeleteCampaign, navigate]
  );

  const handleSelectionChange = useCallback((e) => {
    setSelectedCampaigns(e.value);
  }, []);

  return (
    <div>
      <SimpleBanner
        setShow={setShowSimpleNotification}
        showSimpleNotification={showSimpleNotification}
      />
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center px-4 py-5 card-custom">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Campaigns
          </h1>
          <div className="mt-5 block sm:flex rounded-md space-y-4 sm:space-y-0">
            <div className="relative flex items-stretch flex-grow focus-within:z-10">
              <InputWithSearch
                name="searchCampaigns"
                type="search"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                actionCall={handleSearch}
                labelOn={false}
              />
            </div>

            <div className="flex">
              <button
                className="ml-2 rounded-md bg-main-purple px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={openCreateCampaignSlideOver}
              >
                Add Campaign
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flow-root overflow-x-auto">
        <div className="inline-block min-w-full py-2 align-middle">
          {loading ? (
            <div className="border h-10 shadow rounded-md p-4 w-full mx-auto flex items-center">
              <LoadingSvg />
              <span className="text-gray-600">Loading</span>
            </div>
          ) : (
            <>
              <div className="overflow-hidden card-custom">
                {!campaigns.data || campaigns.data.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500 text-sm">
                    Currently no campaigns
                  </div>
                ) : (
                  <DataTable
                    value={campaigns.data}
                    selection={selectedCampaigns}
                    onSelectionChange={handleSelectionChange}
                    selectionMode="checkbox"
                    dataKey="id"
                    tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                    cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
                    resizableColumns={false}
                    sortable={false}
                  >
                    <Column selectionMode="multiple" headerStyle={{ width: "5%" }} />
                    <Column field="name" header="Name" headerStyle={{ width: "25%" }} />
                    <Column field="location" header="Location" headerStyle={{ width: "25%" }} />
                    <Column header="Start Date" body={startDateBody} headerStyle={{ width: "20%" }} />
                    <Column header="End Date" body={endDateBody} headerStyle={{ width: "20%" }} />
                    <Column header="" body={actionBody} headerStyle={{ width: "25%" }} />
                  </DataTable>
                )}
              </div>
              <div className="w-full">
                <Paginator
                  first={first}
                  rows={rows}
                  totalRecords={campaigns.total ?? 0}
                  onPageChange={handlePagination}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Campaigns)
);
